import { useCallback, useEffect, useState } from "react";
import settings from "../../../constants/settings";
import useFetchAndTrace from "../fetchWithTrace";
import { Board } from "../../../reducers/uploadedBoard";
import { useCustomAlert } from "../alerts";

export interface ContainerAllocationDetails {
  flashpcb_container_barcode: string;
  quantity_allocated: number;
}

export interface ShipmentPartAllocationDetail {
  board: Board;
  shipment_id: string;
  ship_date: string | null;
  required_amount: number;
  allocations: ContainerAllocationDetails[];
  total_allocated_amount: number;
  is_consignment: boolean;
}

export interface ShipmentsPartResponse {
  shipments: ShipmentPartAllocationDetail[];
}

export default function useShipmentsThatNeedPart(
  partId: string | null,
  containerBarcode: string | null,
) {
  const customAlert = useCustomAlert();
  const errorHandler = useCallback(() => {
    customAlert("Failed to get shipments for this part");
  }, [customAlert]);

  const fetchAndTrace = useFetchAndTrace();
  const [shipmentsMissingPart, setShipmentsMissingPart] = useState<
    ShipmentPartAllocationDetail[] | null
  >(null);

  const loadShipmentsHandler = useCallback(
    async (response: Response) => {
      if (response.ok) {
        const { shipments } = (await response.json()) as ShipmentsPartResponse;
        setShipmentsMissingPart(shipments);
      } else if (response.status === 400) {
        const { detail } = (await response.json()) as { detail: string };
        customAlert(detail);
      } else {
        throw Error("Failed to get shipments");
      }
    },
    [customAlert],
  );

  const getShipments = useCallback(() => {
    if (partId || containerBarcode) {
      void fetchAndTrace(
        `${settings.api_url}/inventory/boards-needing-part`,
        loadShipmentsHandler,
        errorHandler,
        {
          body: JSON.stringify({
            part_id: partId,
            container_barcode: containerBarcode,
          }),
          method: "PUT",
        },
      );
    }
  }, [
    errorHandler,
    fetchAndTrace,
    loadShipmentsHandler,
    partId,
    containerBarcode,
  ]);

  useEffect(() => {
    setShipmentsMissingPart(null);
    getShipments();
  }, [partId, getShipments]);

  return { getShipments, shipmentsMissingPart };
}
